import React, { useContext, useEffect } from "react";
import { Box, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useSearchParams } from "react-router-dom";
import MyProjects from "../../components/projects/MyProjects";
import ProjectTenders from "../../components/projects/ProjectTenders";
import AllProjects from "../../components/projects/allProjectsOverview/AllProjects";
import useResponsive from "../../hooks/useResponsive";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import { AuthContext } from "../../context/auth-context/AuthContext";
import ProjectBillingOverview from "../../components/projects/ProjectBillingOverview";
import ProjectKanbanBoard from "../../components/projects/projectKanban/ProjectKanbanBoard";
import useProjects from "../../hooks/projects/useProjects";
import useProjectLeads from "../../hooks/projectLeads/useProjectLeads";

/**
 * ProjectOverview component
 * Handles the tabs for project tenders, my projects and all projects
 * @returns ProjectOverview component
 */
const ProjectOverview = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { allProjects, tenderedProjects, projectsWithBillingCheckmarks } = useProjects();
  const { projectLeads } = useProjectLeads();
  const isMobile = useResponsive("down", "sm");
  const { auth } = useContext(AuthContext);
  const hasBillingPermission = doesPermissionsHaveSomeOf(auth.permissions, [7]);
  const tabFromUrl = searchParams.get("tab");
  const allowedTabs = hasBillingPermission
    ? ["overview", "projectTenders", "myProjects", "allProjects", "billing"]
    : ["overview", "projectTenders", "myProjects", "allProjects"];
  const fallbackTab = "projectTenders";
  const isAllowedTab = tabFromUrl ? allowedTabs.includes(tabFromUrl) : false;
  const value = tabFromUrl && isAllowedTab ? tabFromUrl : fallbackTab;

  useEffect(() => {
    if (!tabFromUrl || isAllowedTab) {
      return;
    }

    setSearchParams(
      (params) => {
        const nextParams = new URLSearchParams(params);
        nextParams.set("tab", fallbackTab);
        return nextParams;
      },
      { replace: true }
    );
  }, [fallbackTab, isAllowedTab, setSearchParams, tabFromUrl]);

  // the user's projects but only the projects in "Angebot", "Durchführung", "Abrechnung" or "Abgeschlossen"
  const myProjects = allProjects.filter(
    (project) =>
      (project.status === "Angebot" ||
        project.status === "Durchführung" ||
        project.status === "Abrechnung" ||
        project.status === "Abgeschlossen") &&
      project.projectMembers.some((member) => member.memberId === auth.userID)
  );

  // Handles the tab change
  const handleChangeTab = (event: React.SyntheticEvent, newValue: string) => {
    setSearchParams({ tab: newValue });
  };

  return (
    <TabContext value={value}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <TabList
          onChange={handleChangeTab}
          variant={isMobile ? "scrollable" : "standard"}
          centered={isMobile ? false : true}
        >
          <Tab label="Übersicht" value="overview" />
          <Tab label="Projektausschreibungen" value="projectTenders" />
          <Tab label="Meine Projekte" value="myProjects" />
          <Tab label="Alle Projekte" value="allProjects" />
          {hasBillingPermission ? <Tab label="Abrechnung" value="billing" /> : null}
        </TabList>
      </Box>
      <TabPanel value="overview" sx={{ px: 0 }}>
        <ProjectKanbanBoard projects={allProjects} projectLeads={projectLeads} />
      </TabPanel>
      <TabPanel value="projectTenders">
        <ProjectTenders tenderedProjects={tenderedProjects} />
      </TabPanel>
      <TabPanel value="myProjects">
        <MyProjects projects={myProjects} />
      </TabPanel>
      <TabPanel value="allProjects">
        <AllProjects projects={allProjects} />
      </TabPanel>
      <TabPanel value="billing">
        <ProjectBillingOverview projectsWithBillingCheckmarks={projectsWithBillingCheckmarks} />
      </TabPanel>
    </TabContext>
  );
};

export default ProjectOverview;
